import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  FlatList,
  RefreshControl,
  Platform,
} from "react-native";
import { platformAlert as showAlert } from "../utils/platformAlert";
import { Feather } from "@expo/vector-icons";
import { spacing } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { useStripe } from "../hooks/useStripe";
import { supabase } from "../lib/supabase";
import { useQueryClient } from "@tanstack/react-query";

type CardPM = {
  stripe_payment_method_id: string;
  card_brand: string | null;
  card_last4: string | null;
  card_exp_month: number | null;
  card_exp_year: number | null;
  is_default: boolean | null;
  status: string | null;
  type: "card";
};

export default function PaymentMethodsScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const { initPaymentSheet, presentPaymentSheet } = useStripe();
  const queryClient = useQueryClient();

  // Global states
  const [isAdding, setIsAdding] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Data
  const [items, setItems] = useState<CardPM[]>([]);
  const [customerId, setCustomerId] = useState<string | null>(null);

  // Per-item action loaders
  const [settingDefault, setSettingDefault] = useState<Record<string, boolean>>(
    {}
  );
  const [removing, setRemoving] = useState<Record<string, boolean>>({});

  // Prevent double-submit taps
  const tapGuard = useRef(false);

  useEffect(() => {
    (async () => {
      await ensureCustomer();
      await fetchPaymentMethods();
    })();
  }, []);

  const ensureCustomer = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return;

    const resp = await fetch(
      "https://wgghxlrehnnejmvzzsmx.functions.supabase.co/get-or-create-customer",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const json = await resp.json();
    if (resp.ok && json.customerId) setCustomerId(json.customerId);
  };

  const fetchPaymentMethods = async () => {
    try {
      setIsFetching(true);
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const { data, error } = await supabase
        .from("payment_methods")
        .select(
          "stripe_payment_method_id, card_brand, card_last4, card_exp_month, card_exp_year, is_default, status, type"
        )
        .eq("user_id", session?.user?.id)
        .eq("type", "card")
        .neq("status", "detached")
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setItems((data as CardPM[]) ?? []);
    } catch (e) {
      console.error("Error fetching payment methods:", e);
      showAlert("Error", "Could not load your cards.");
    } finally {
      setIsFetching(false);
      setIsRefreshing(false);
    }
  };

  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchPaymentMethods();
  }, []);

  const handleAddCard = async (retryCount: number = 0) => {
    if (tapGuard.current && retryCount === 0) return; // Only block on first call
    if (retryCount > 0) {
      tapGuard.current = true; // Re-enable guard for retries
    } else {
      if (tapGuard.current) return;
      tapGuard.current = true;
    }

    // Prevent infinite retry loops
    if (retryCount >= 2) {
      console.error("[DEBUG] Max retries reached for setup intent. Retry count:", retryCount);
      showAlert(
        "Error", 
        "Failed to add card. This may be due to a Stripe key mismatch. Please restart the app and try again."
      );
      setIsAdding(false);
      tapGuard.current = false;
      return;
    }

    try {
      setIsAdding(true);

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("You must be logged in to add a card");

      if (!customerId) await ensureCustomer();

      const siRes = await fetch(
        "https://wgghxlrehnnejmvzzsmx.functions.supabase.co/create-setup-intent",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const siJson = await siRes.json();
      if (!siRes.ok || !siJson?.clientSecret) {
        throw new Error(siJson?.error || "Failed to create setup intent");
      }
      const clientSecret: string = siJson.clientSecret;
      if (
        !clientSecret.startsWith("seti_") ||
        !clientSecret.includes("_secret_")
      ) {
        throw new Error("Server returned invalid SetupIntent client_secret");
      }

      console.log("[DEBUG] initPaymentSheet starting with secret:", clientSecret);

      const { error: initError } = await initPaymentSheet({
        merchantDisplayName: "JadeTrade",
        setupIntentClientSecret: clientSecret,
        returnURL: "com.jadetrade://stripe-redirect",
        appearance: { colors: { primary: colors.primary } },
      });

      if (initError) {
        console.error("[DEBUG] initPaymentSheet failed:", initError);
        throw new Error(initError.message);
      }

      console.log("[DEBUG] initPaymentSheet success, presenting sheet...");
      const { error: presentError } = await presentPaymentSheet();
      if (presentError) {
        if (presentError.code === "Canceled") {
          console.log("[DEBUG] User canceled PaymentSheet.");
          return; // silent exit, not an actual failure
        }
        
        // If setup intent is missing/invalid, create a new one and retry
        if (presentError.message?.includes("No such setupintent") || 
            presentError.stripeErrorCode === "resource_missing") {
          const currentRetry = typeof retryCount === 'number' ? retryCount : 0;
          const nextRetry = currentRetry + 1;
          console.log("[DEBUG] Setup intent expired or invalid, creating new one... (retry:", nextRetry, "/2)");
          
          // Check retry limit before retrying
          if (nextRetry >= 2) {
            console.error("[DEBUG] Max retries reached for setup intent. This likely indicates a Stripe key mismatch.");
            setIsAdding(false);
            tapGuard.current = false;
            showAlert(
              "Stripe Key Mismatch", 
              "Failed to add card. The app's Stripe key doesn't match the server. Please restart the app to reload the correct keys."
            );
            return;
          }
          
          // Retry by calling handleAddCard again (will create new setup intent)
          setIsAdding(false);
          tapGuard.current = false;
          // Small delay to prevent immediate retry
          setTimeout(() => {
            handleAddCard(nextRetry);
          }, 1000);
          return;
        }
        
        console.error("[DEBUG] presentPaymentSheet failed:", presentError);
        throw new Error(presentError.message);
      }

      console.log("[DEBUG] presentPaymentSheet completed successfully");

      // Trigger backend sync as fallback (in case webhook missed)
      let currentSession = null;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        currentSession = session;
        if (session?.access_token) {
          console.log("[DEBUG] invoking sync-payment-methods...");
          const syncRes = await supabase.functions.invoke('sync-payment-methods', {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          console.log("[DEBUG] sync-payment-methods result:", syncRes);
        }
      } catch (syncErr) {
        console.error("[DEBUG] sync-payment-methods failed:", syncErr);
      }

      // Invalidate React Query cache so CheckoutScreen and other screens see the new card
      // Get current user ID to invalidate only their cache
      if (!currentSession) {
        const { data: { session } } = await supabase.auth.getSession();
        currentSession = session;
      }
      const userId = currentSession?.user?.id;
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ['payment-methods', userId] });
      }
      
      // Reload list (webhook inserts/syncs)
      await fetchPaymentMethods();
    } catch (e: any) {
      console.log("Add card error:", e);
      showAlert("Error", e?.message || "Failed to add card");
    } finally {
      setIsAdding(false);
      tapGuard.current = false;
    }
  };

  const detach = useCallback(
    async (pmId: string) => {
      if (removing[pmId]) return;
      setRemoving((m) => ({ ...m, [pmId]: true }));

      try {
        // optimistic UI
        const prev = items;
        setItems((arr) =>
          arr.filter((x) => x.stripe_payment_method_id !== pmId)
        );

        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token!;
        const resp = await fetch(
          "https://wgghxlrehnnejmvzzsmx.functions.supabase.co/detach-payment-method",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ paymentMethodId: pmId }),
          }
        );

        if (!resp.ok) {
          setItems(prev); // rollback
          const j = await resp.json().catch(() => ({}));
          throw new Error(j.error || "Failed to remove card");
        }
      } catch (e: any) {
        showAlert("Error", e?.message || "Failed to remove card");
      } finally {
        setRemoving((m) => {
          const { [pmId]: _, ...rest } = m;
          return rest;
        });
      }
    },
    [items, removing]
  );

  const makeDefault = useCallback(
    async (pmId: string) => {
      if (settingDefault[pmId]) return;
      setSettingDefault((m) => ({ ...m, [pmId]: true }));

      try {
        if (!customerId) throw new Error("Customer is not ready yet");

        // optimistic UI
        setItems((arr) =>
          arr.map((x) => ({
            ...x,
            is_default: x.stripe_payment_method_id === pmId,
          }))
        );

        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token!;
        const resp = await fetch(
          "https://wgghxlrehnnejmvzzsmx.functions.supabase.co/set-default-payment-method",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ customerId, paymentMethodId: pmId }),
          }
        );
        if (!resp.ok) {
          // rollback by refetch
          await fetchPaymentMethods();
          const j = await resp.json().catch(() => ({}));
          throw new Error(j.error || "Failed to set default");
        }

        // ensure UI === DB (even if webhook is slow)
        await fetchPaymentMethods();
      } catch (e: any) {
        showAlert("Error", e?.message || "Failed to set default");
      } finally {
        setSettingDefault((m) => {
          const { [pmId]: _, ...rest } = m;
          return rest;
        });
      }
    },
    [customerId, settingDefault]
  );

  const BrandIcon = ({ brand }: { brand?: string | null }) => {
    const icon = useMemo(() => {
      switch ((brand || "").toLowerCase()) {
        case "visa":
        case "mastercard":
        case "amex":
        case "discover":
        default:
          return "credit-card";
      }
    }, [brand]);
    return <Feather name={icon as any} size={22} color={colors.primary} />;
  };

  const renderSkeleton = () => (
    <>
      {[0, 1].map((k) => (
        <View key={`sk-${k}`} style={[styles.card, styles.skeletonCard]}>
          <View style={[styles.skelLine, { width: 120 }]} />
          <View style={[styles.skelLine, { width: "60%", marginTop: 10 }]} />
          <View style={[styles.skelLine, { width: "40%", marginTop: 10 }]} />
        </View>
      ))}
    </>
  );

  const renderItem = ({ item }: { item: CardPM }) => {
    const exp =
      item.card_exp_month && item.card_exp_year
        ? `${String(item.card_exp_month).padStart(2, "0")}/${String(
            item.card_exp_year
          ).slice(-2)}`
        : "—";

    const isDefault = !!item.is_default;
    const pmId = item.stripe_payment_method_id;
    const isSetting = !!settingDefault[pmId];
    const isRemoving = !!removing[pmId];

    return (
      <View style={[styles.card, isDefault ? styles.cardDefault : null]}>
        <View style={styles.cardHeader}>
          <View style={styles.brandRow}>
            <BrandIcon brand={item.card_brand} />
            <Text style={styles.brandText}>
              {(item.card_brand || "Card").toUpperCase()}
            </Text>
            {isDefault ? (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>DEFAULT</Text>
              </View>
            ) : null}
          </View>
          <Text style={styles.last4}>•••• {item.card_last4 ?? "????"}</Text>
        </View>

        <View style={styles.metaRow}>
          <Text style={styles.metaLabel}>Expires</Text>
          <Text style={styles.metaValue}>{exp}</Text>
        </View>

        <View style={styles.actionsRow}>
          {!isDefault ? (
            <TouchableOpacity
              style={[
                styles.actionBtn,
                styles.primaryBtn,
                isSetting && styles.actionDisabled,
              ]}
              onPress={() => makeDefault(pmId)}
              disabled={isSetting || isRemoving || isAdding || isFetching}
            >
              {isSetting ? (
                <ActivityIndicator size="small" color={colors.surface} />
              ) : (
                <Feather name="star" size={16} color={colors.surface} />
              )}
              <Text style={styles.actionPrimaryText}>
                {isSetting ? "Setting…" : "Set default"}
              </Text>
            </TouchableOpacity>
          ) : (
            <View style={{ width: 1 }} />
          )}

          <TouchableOpacity
            style={[
              styles.actionBtn,
              styles.dangerBtn,
              isRemoving && styles.actionDisabled,
            ]}
            onPress={() =>
              showAlert(
                "Remove card",
                "Are you sure you want to remove this card?",
                [
                  { text: "Cancel", style: "cancel" },
                  {
                    text: "Remove",
                    style: "destructive",
                    onPress: () => detach(pmId),
                  },
                ]
              )
            }
            disabled={isRemoving || isSetting || isAdding || isFetching}
          >
            {isRemoving ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Feather name="trash-2" size={16} color={colors.surface} />
            )}
            <Text style={styles.actionDangerText}>
              {isRemoving ? "Removing…" : "Remove"}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <ScrollView
      style={styles.container}
      contentInsetAdjustmentBehavior="automatic"
      refreshControl={
        <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
      }
    >
      {/* Add Card CTA */}
      <View style={styles.methodsContainer}>
        <TouchableOpacity
          style={[
            styles.addButton,
            (isAdding || isFetching) && styles.disabled,
          ]}
          onPress={handleAddCard}
          disabled={isAdding || isFetching}
          activeOpacity={0.8}
        >
          {isAdding ? (
            <ActivityIndicator />
          ) : (
            <Feather name="credit-card" size={24} color={colors.primary} />
          )}
          <View style={styles.buttonTextContainer}>
            <Text style={styles.addButtonText}>
              {isAdding ? "Preparing…" : "Add Credit/Debit Card"}
            </Text>
            <Text style={styles.buttonSubtext}>
              Securely add your card details
            </Text>
          </View>
          <Feather name="chevron-right" size={24} color={colors.primary} />
        </TouchableOpacity>
      </View>

      {/* List header */}
      <View style={styles.listHeader}>
        <Text style={styles.sectionTitle}>Your cards</Text>
        {isFetching ? <ActivityIndicator /> : null}
      </View>

      {/* Skeletons / Empty / List */}
      {isFetching ? (
        <View style={{ paddingHorizontal: spacing.md }}>
          {renderSkeleton()}
        </View>
      ) : items.length === 0 ? (
        <View style={styles.emptyBox}>
          <Feather name="credit-card" size={22} color={colors.textLight} />
          <Text style={styles.emptyText}>No cards yet</Text>
        </View>
      ) : (
        <FlatList
          data={items}
          keyExtractor={(x) => x.stripe_payment_method_id}
          renderItem={renderItem}
          scrollEnabled={false}
          contentContainerStyle={{
            paddingHorizontal: spacing.md,
            paddingBottom: spacing.lg,
          }}
        />
      )}

      {/* Info */}
      <View style={styles.infoBox}>
        <Feather name="shield" size={20} color={colors.primary} />
        <Text style={styles.infoText}>
          By providing your card information, you authorize Stripe to process
          payments on behalf of JadeTrade in accordance with JadeTrade’s terms.
        </Text>
      </View>
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    methodsContainer: { margin: spacing.md },
    addButton: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.background,
      padding: spacing.md,
      borderRadius: 14,
      marginBottom: spacing.sm,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.12,
      shadowRadius: 6,
      elevation: 4,
    },
    disabled: { opacity: 0.6 },
    buttonTextContainer: { flex: 1, marginLeft: spacing.md },
    addButtonText: { fontSize: 16, fontWeight: "700", color: colors.text },
    buttonSubtext: { fontSize: 13, color: colors.textLight, marginTop: 2 },

    listHeader: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    sectionTitle: { fontSize: 16, fontWeight: "700", color: colors.text },

    card: {
      backgroundColor: colors.background,
      borderRadius: 16,
      padding: spacing.md,
      marginVertical: spacing.sm,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.08,
      shadowRadius: 10,
      elevation: 3,
    },
    cardDefault: {
      borderColor: colors.primary,
      borderWidth: 1,
    },
    cardHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    brandRow: { flexDirection: "row", alignItems: "center" },
    brandText: { marginLeft: 8, fontWeight: "700", color: colors.text },
    last4: { fontWeight: "700", letterSpacing: 1, color: colors.text },

    metaRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: spacing.sm,
      paddingVertical: 6,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    metaLabel: { color: colors.textLight, fontSize: 13 },
    metaValue: { color: colors.text, fontWeight: "600" },

    actionsRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: spacing.sm,
      gap: spacing.sm,
    },
    actionBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderRadius: 12,
    },
    actionDisabled: { opacity: 0.6 },
    primaryBtn: { backgroundColor: colors.primary },
    dangerBtn: { backgroundColor: colors.error },
    actionPrimaryText: { color: colors.surface, fontWeight: "700" },
    actionDangerText: { color: colors.surface, fontWeight: "700" },

    badge: {
      marginLeft: 10,
      paddingHorizontal: 8,
      paddingVertical: 2,
      backgroundColor: colors.surface,
      borderRadius: 8,
    },
    badgeText: { color: colors.primary, fontSize: 11, fontWeight: "800" },

    emptyBox: {
      marginHorizontal: spacing.md,
      marginTop: spacing.md,
      padding: spacing.lg,
      borderRadius: 14,
      alignItems: "center",
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.border,
      gap: 8,
    },
    emptyText: { fontSize: 16, color: colors.textLight },

    infoBox: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.background,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.md,
      borderRadius: 12,
    },
    infoText: {
      flex: 1,
      marginLeft: spacing.sm,
      fontSize: 14,
      color: colors.textLight,
      lineHeight: 20,
    },

    // “Vanilla” skeleton style
    skeletonCard: {
      overflow: "hidden",
    },
    skelLine: {
      height: 12,
      backgroundColor: colors.surface,
      borderRadius: 6,
    },
  });
